import * as React from 'react'
import { connect } from 'react-redux'

import {
  applicationStepLoaded,
  applicationStepSelected,
  fetchRouterQuestions,
  fetchStateFromIP,
  stepSelected,
  updateLead,
} from 'common/actions'

import {
  applicationCalculateQuestionMachineConfig,
  applicationFieldBlurred,
  applicationFieldChanged,
  applicationFieldShowValidationStatus,
  applicationFieldUnregistered,
  applicationValidate,
} from 'common/actions/application'

import {
  ApplicationStep,
  Field,
  FieldKey,
  FieldType,
  LifeInsuranceApplicationStep,
  LifeInsuranceQuestionGroups,
  MultiProductDispatch,
} from 'embed/constants'
import { QuestionMachineConfig } from 'embed/constants/life-insurance'
import {
  applicationLifeInsuranceRouterQuestionsGroupsSelector,
  applicationLifeInsuranceRouterQuestionsMachineConfigSelector,
  lifeInsuranceApplicationFieldsSelector,
} from 'embed/selectors/application'
import { configStepSelector } from 'embed/selectors/config'
import { filtersLifeInsuranceStateSelector } from 'embed/selectors/filters'
import { loadingSelector } from 'embed/selectors/loading'
import Parent from 'common/services/Parent'
import { Fields } from 'embed/constants/fields'
import { uiHasCalledGeocoderSelector, uiSelectedLifeInsuranceApplicationStepSelector } from 'embed/selectors/ui'
import { LifeInsuranceStep, Product } from 'even-ts-static'

import { MultiProductState } from 'embed/reducers'
import { ApplicationStepDateOfBirth } from './Steps/date-of-birth'
import { ApplicationStepGender } from './Steps/gender'
import { ApplicationStepHeight } from './Steps/height'
import { RestartApplication } from './Steps/restart-application'
import { RouterQuestionsContainer } from './Steps/router-questions-container'
import { ApplicationStepStateOfResidence } from './Steps/state-of-residence'
import { ApplicationStepTobaccoUse } from './Steps/tobacco-use'
import { ApplicationStepWeight } from './Steps/weight'

interface StoreProps {
  directReferral: boolean
  filterState: string | undefined
  groups: LifeInsuranceQuestionGroups
  hasCalledGeocoder: boolean
  isLoading: boolean
  stepByStepApplicationStep: LifeInsuranceApplicationStep | undefined
  stepByStepApplicationStepIsValid: boolean
  questionMachineConfig: QuestionMachineConfig | undefined
}

interface DispatchProps {
  dispatchApplicationFieldBlurred: (field: Field) => void
  dispatchApplicationFieldChanged: (field: Field, value: unknown) => void
  dispatchApplicationFieldShowValidationStatus: (fieldKey: FieldKey, show: boolean) => void
  dispatchApplicationFieldUnregistered: (field: Field) => void
  dispatchApplicationLoaded: () => void
  dispatchApplicationStepSelected: (applicationStep: ApplicationStep) => void
  dispatchBackToStepOne: () => void
  dispatchConsentsToFCRAandTCPA: () => void
  dispatchContinueToResults: () => void
  dispatchFetchRouterQuestions: () => void
  dispatchFetchStateFromIP: () => void
  dispatchUpdateLead: () => void
  dispatchApplicationCalculateQuestionMachineConfig: () => void
}

export type StepbystepApplicationContainerProps = StoreProps & DispatchProps

export interface LifeInsuranceApplicationProps {
  onBackToStepOne: () => void
  onBack: () => void
  onContinue: () => void
  stepByStepApplicationStepIsValid: boolean
  setIsBeforeRouterQuestions: (isBeforeRouterQuestions: boolean) => void
  currentStep: number
  totalNumberOfSteps: number
}

const getApplicationSteps = (): LifeInsuranceApplicationStep[] => [
  LifeInsuranceApplicationStep.RestartApplication,
  LifeInsuranceApplicationStep.DateOfBirth,
  LifeInsuranceApplicationStep.Gender,
  LifeInsuranceApplicationStep.HeightInInches,
  LifeInsuranceApplicationStep.WeightInPounds,
  LifeInsuranceApplicationStep.UsesTobacco,
  LifeInsuranceApplicationStep.RouterQuestions,
  LifeInsuranceApplicationStep.StateOfResidence,
]

const getNumberOfTopLevelRouterQuestions = (groups: LifeInsuranceQuestionGroups): number =>
  groups.reduce((final, group) => [...final, ...group], []).length

const StepbystepApplicationContainer: React.FC<StepbystepApplicationContainerProps> = ({
  dispatchApplicationCalculateQuestionMachineConfig,
  dispatchApplicationFieldChanged,
  dispatchApplicationFieldShowValidationStatus,
  dispatchApplicationStepSelected,
  dispatchBackToStepOne,
  dispatchConsentsToFCRAandTCPA,
  dispatchContinueToResults,
  dispatchFetchRouterQuestions,
  dispatchFetchStateFromIP,
  filterState,
  groups,
  hasCalledGeocoder,
  questionMachineConfig,
  stepByStepApplicationStep,
  stepByStepApplicationStepIsValid,
  ...other
}) => {
  // ms delay for button list handleChangeAndContinue and progressBar totalNumberOfSteps updating
  const delay = 200

  const initialStep =
    // Set to number of router questions if we're already past the router questions
    stepByStepApplicationStep !== undefined &&
    getApplicationSteps().indexOf(stepByStepApplicationStep) >
      getApplicationSteps().indexOf(LifeInsuranceApplicationStep.RouterQuestions)
      ? getNumberOfTopLevelRouterQuestions(groups)
      : 0

  const [currentRouterStep, setCurrentRouterStep] = React.useState(initialStep)
  const [followupStep, setFollowupStep] = React.useState(0)
  const [isBeforeRouterQuestions, setIsBeforeRouterQuestions] = React.useState(false)
  const [currentStep, setCurrentStep] = React.useState(0)
  const [totalNumberOfSteps, setTotalNumberOfSteps] = React.useState(getNumberOfTopLevelRouterQuestions(groups) - 1)

  React.useEffect(() => {
    Parent.scrollToFrame()

    setTotalNumberOfSteps(getApplicationSteps().length + getNumberOfTopLevelRouterQuestions(groups) - 1)

    if (stepByStepApplicationStep !== undefined) {
      const step =
        stepByStepApplicationStep === LifeInsuranceApplicationStep.RestartApplication
          ? 0
          : getApplicationSteps().indexOf(stepByStepApplicationStep)
      setCurrentStep(step)
    } else {
      dispatchApplicationStepSelected(LifeInsuranceApplicationStep.RestartApplication)
      setCurrentStep(0)
    }

    if (!hasCalledGeocoder) {
      dispatchFetchStateFromIP()
    }

    if (groups.length === 0) {
      dispatchFetchRouterQuestions()
    }
  }, [])

  React.useEffect(() => {
    if (filterState) {
      dispatchApplicationFieldChanged(Fields.State, filterState)
    }
  }, [dispatchApplicationFieldChanged, filterState])

  React.useEffect(() => {
    if (groups.length > 0) {
      setTotalNumberOfSteps(totalNumberOfSteps + getNumberOfTopLevelRouterQuestions(groups) - 1)
    }
  }, [groups.length])

  React.useEffect(() => {
    if (questionMachineConfig === undefined) {
      const currentStepIdx =
        stepByStepApplicationStep !== undefined && getApplicationSteps().indexOf(stepByStepApplicationStep)
      const routerQuestionsStepIdx = getApplicationSteps().indexOf(LifeInsuranceApplicationStep.RouterQuestions)
      if (currentStepIdx === routerQuestionsStepIdx - 1) {
        dispatchApplicationCalculateQuestionMachineConfig()
      }
    }
  }, [stepByStepApplicationStep])

  const onContinueToFirstApplicationStep = React.useCallback(() => {
    setCurrentStep(1)
    dispatchApplicationStepSelected(getApplicationSteps()[1])
    Parent.scrollToFrame()
  }, [dispatchApplicationStepSelected])

  const onContinue = React.useCallback(
    (shouldSkipValidate?: boolean) => {
      const stepValues: LifeInsuranceApplicationStep[] = getApplicationSteps()
      const currentApplicationStep = stepByStepApplicationStep ?? LifeInsuranceApplicationStep.RestartApplication
      const currentStepIndex = stepValues.indexOf(currentApplicationStep)
      const nextStep = stepValues[currentStepIndex + 1]
      const nextStepIndex = stepValues.indexOf(nextStep)
      const isLastStep = currentStep + currentRouterStep === totalNumberOfSteps

      if (stepByStepApplicationStepIsValid && isLastStep) {
        dispatchContinueToResults()
      } else if (
        stepByStepApplicationStep === LifeInsuranceApplicationStep.RestartApplication ||
        stepByStepApplicationStepIsValid ||
        shouldSkipValidate
      ) {
        setCurrentStep(nextStepIndex)
        dispatchApplicationStepSelected(nextStep)

        // Clear the currently focused element
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur()
        }

        // Scroll to the top of the embed after each question is answered
        Parent.scrollToFrame()
      } else {
        if (stepByStepApplicationStep !== undefined) {
          // Show which fields are invalid for this step
          FieldsForStepValidation[stepByStepApplicationStep].forEach((fieldKey) => {
            dispatchApplicationFieldShowValidationStatus(fieldKey, true)
          })
        }
      }
    },
    [
      currentRouterStep,
      currentStep,
      dispatchApplicationFieldShowValidationStatus,
      dispatchApplicationStepSelected,
      dispatchContinueToResults,
      stepByStepApplicationStep,
      stepByStepApplicationStepIsValid,
      totalNumberOfSteps,
    ],
  )

  const onBack = React.useCallback(() => {
    if (stepByStepApplicationStep !== undefined) {
      const stepValues: LifeInsuranceApplicationStep[] = getApplicationSteps()
      const currentStepIndex = stepValues.indexOf(stepByStepApplicationStep)
      const previousStep = stepValues[currentStepIndex - 1]
      const previousStepIndex = stepValues.indexOf(previousStep)
      setCurrentStep(previousStepIndex)
      dispatchApplicationStepSelected(previousStep)
    }
  }, [dispatchApplicationStepSelected, stepByStepApplicationStep])

  const handleChangeAndContinue = React.useCallback(
    (field: Field, value: string) => {
      if (field.type === FieldType.ButtonList) {
        // Delay continuing slightly, so you briefly see the active state of the button
        setTimeout(() => {
          onContinue(true)
        }, delay)
      }
      dispatchApplicationFieldChanged(field, value)
    },
    [dispatchApplicationFieldChanged, onContinue],
  )

  const applicationProps = {
    ...other,
    currentStep: currentStep + currentRouterStep,
    handleChangeAndContinue,
    onBack,
    onBackToStepOne: dispatchBackToStepOne,
    onContinue,
    setIsBeforeRouterQuestions,
    stepByStepApplicationStep,
    stepByStepApplicationStepIsValid,
    totalNumberOfSteps,
  }

  switch (stepByStepApplicationStep) {
    case LifeInsuranceApplicationStep.StateOfResidence: {
      return (
        <ApplicationStepStateOfResidence
          {...applicationProps}
          handleConsentsToFCRAandTCPA={dispatchConsentsToFCRAandTCPA}
        />
      )
    }
    case LifeInsuranceApplicationStep.UsesTobacco: {
      return <ApplicationStepTobaccoUse {...applicationProps} />
    }
    case LifeInsuranceApplicationStep.HeightInInches: {
      return <ApplicationStepHeight {...applicationProps} />
    }
    case LifeInsuranceApplicationStep.WeightInPounds: {
      return <ApplicationStepWeight {...applicationProps} />
    }
    case LifeInsuranceApplicationStep.Gender: {
      return <ApplicationStepGender {...applicationProps} />
    }
    case LifeInsuranceApplicationStep.RouterQuestions: {
      const routerQuestionsProps = {
        ...applicationProps,
        followupStep,
        groups,
        setCurrentRouterStep,
        setFollowupStep,
        shouldReplayResponses: !isBeforeRouterQuestions,
      }
      return <RouterQuestionsContainer {...routerQuestionsProps} />
    }
    case LifeInsuranceApplicationStep.DateOfBirth: {
      return <ApplicationStepDateOfBirth {...applicationProps} />
    }
    case LifeInsuranceApplicationStep.RestartApplication:
      return (
        <RestartApplication {...applicationProps} onContinueToFirstApplicationStep={onContinueToFirstApplicationStep} />
      )
    default: {
      return null
    }
  }
}

const FieldsForStepValidation = {
  [LifeInsuranceApplicationStep.DateOfBirth]: [Fields.DateOfBirth.key],
  [LifeInsuranceApplicationStep.Gender]: [Fields.Gender.key],
  [LifeInsuranceApplicationStep.HeightInInches]: [Fields.HeightInInches.key],
  [LifeInsuranceApplicationStep.WeightInPounds]: [Fields.WeightInPounds.key],
  [LifeInsuranceApplicationStep.UsesTobacco]: [Fields.TobaccoSmoker.key],
  [LifeInsuranceApplicationStep.StateOfResidence]: [Fields.State.key],
}

const mapStateToProps = (state: MultiProductState): StoreProps => {
  const stepByStepApplicationStep = uiSelectedLifeInsuranceApplicationStepSelector(state)
  const lifeInsuranceApplicationFields = lifeInsuranceApplicationFieldsSelector(state) || {}
  const hasCalledGeocoder = uiHasCalledGeocoderSelector(state)

  const isLoading = loadingSelector(state, Product.LifeInsurance)

  let stepByStepApplicationStepIsValid = true
  if (isLoading) {
    stepByStepApplicationStepIsValid = false
  } else if (
    stepByStepApplicationStep !== undefined &&
    stepByStepApplicationStep !== LifeInsuranceApplicationStep.RouterQuestions &&
    stepByStepApplicationStep !== LifeInsuranceApplicationStep.RestartApplication
  ) {
    const field = lifeInsuranceApplicationFields[FieldsForStepValidation[stepByStepApplicationStep][0]]
    stepByStepApplicationStepIsValid = !field || !!field.isValid
  }

  const filterState = filtersLifeInsuranceStateSelector(state)

  const groups = applicationLifeInsuranceRouterQuestionsGroupsSelector(state)

  const questionMachineConfig = applicationLifeInsuranceRouterQuestionsMachineConfigSelector(state)

  const directReferral = configStepSelector(state) === LifeInsuranceStep.Application

  return {
    directReferral,
    filterState,
    groups,
    hasCalledGeocoder,
    isLoading,
    questionMachineConfig,
    stepByStepApplicationStep,
    stepByStepApplicationStepIsValid,
  }
}

const mapDispatchToProps = (dispatch: MultiProductDispatch): DispatchProps => ({
  dispatchApplicationFieldBlurred: (field: Field) =>
    dispatch(applicationFieldBlurred(Product.LifeInsurance, field.key)),
  dispatchApplicationFieldChanged: (field: Field, value: string | number) =>
    dispatch(applicationFieldChanged(Product.LifeInsurance, field.key, value)),
  dispatchApplicationFieldShowValidationStatus: (fieldKey: FieldKey, show: boolean) =>
    dispatch(applicationFieldShowValidationStatus(Product.LifeInsurance, fieldKey, show)),
  dispatchApplicationFieldUnregistered: (field: Field) =>
    dispatch(applicationFieldUnregistered(Product.LifeInsurance, field.key)),
  dispatchApplicationLoaded: () => dispatch(applicationStepLoaded()),
  dispatchApplicationStepSelected: (applicationStep: ApplicationStep) =>
    dispatch(applicationStepSelected(Product.LifeInsurance, applicationStep)),
  dispatchBackToStepOne: () => dispatch(stepSelected(Product.LifeInsurance, LifeInsuranceStep.Preview)),
  dispatchConsentsToFCRAandTCPA: () =>
    Promise.all([
      dispatch(applicationFieldChanged(Product.LifeInsurance, Fields.ConsentsToFCRA.key, true)),
      dispatch(applicationFieldChanged(Product.LifeInsurance, Fields.ConsentsToTCPA.key, true)),
    ]),
  dispatchContinueToResults: () => dispatch(applicationValidate(Product.LifeInsurance, LifeInsuranceStep.Results)),
  dispatchFetchStateFromIP: () => dispatch(fetchStateFromIP(Product.LifeInsurance)),
  dispatchUpdateLead: () => dispatch(updateLead(Product.LifeInsurance)),
  dispatchFetchRouterQuestions: () => dispatch(fetchRouterQuestions()),
  dispatchApplicationCalculateQuestionMachineConfig: () => dispatch(applicationCalculateQuestionMachineConfig()),
})

export const ConnectedStepbystepApplicationContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(StepbystepApplicationContainer)
